import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxCsvParser, NgxCSVParserError } from 'ngx-csv-parser';
import { ToastrService } from 'ngx-toastr';
import { finalize, Observable, Subscription } from 'rxjs';
import { FormControl } from '@angular/forms';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideSearch } from '@ng-icons/lucide';
import { ColumnDef } from '@3ddv/software-division-components/backoffice/table';
import { ButtonComponent } from '@3ddv/software-division-components/generic/button';
import { InputComponent } from '@3ddv/software-division-components/backoffice/input';
import { GsTableComponent } from '../../shared/ui/table/gs-table.component';
import {
  AppDialogService,
  DialogCloseReason,
} from '../layout/dialog/app-dialog.service';
import { PaginationComponent } from '../layout/pagination/pagination.component';
import {
  ModalAction,
  ModalUserComponent,
} from './modals/modal-user/modal-user.component';
import { UploadResultComponent } from './modals/upload-result/upload-result.component';
import { User } from './models/users.model';
import { UsersResolveValue } from './resolvers/users.resolver';
import { UsersService } from './services/users.service';
import { buildUserColumns } from './users.columns';

const CSV_HEADERS = ['username', 'password', 'email', 'type'];

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.css'],
  imports: [
    ButtonComponent,
    InputComponent,
    NgIcon,
    GsTableComponent,
    PaginationComponent,
  ],
  viewProviders: [provideIcons({ lucideSearch })],
})
export class UsersComponent implements OnInit, OnDestroy {
  private usersService = inject(UsersService);
  private dialogService = inject(AppDialogService);
  private activatedRoute = inject(ActivatedRoute);
  private ngxCsvParser = inject(NgxCsvParser);
  private toastr = inject(ToastrService);
  private router = inject(Router);

  public data: Array<User>;
  public loading = signal(false);
  public activeSearch: boolean;
  private subscriptions: Array<Subscription> = [];
  public currentPage: number;
  public totalItemsInDb: number;
  public itemsPerPage: number;
  public search: { value: string; applied: boolean };
  public columns: ColumnDef<User>[] = [];
  public searchControl = new FormControl('', { nonNullable: true });

  @ViewChild('pagination') pagination: PaginationComponent;
  @ViewChild('csvFile', { static: false })
  csvFileRef!: ElementRef;

  constructor() {
    this.subscriptions = [];
    this.initializeSearch();
  }

  ngOnInit(): void {
    this.columns = buildUserColumns(
      user => this.openModalEdit(user),
      user => this.openModalDelete(user)
    );

    const resolved: UsersResolveValue =
      this.activatedRoute.snapshot.data['api'];

    if (resolved.error) {
      this.toastr.error('Error loading users');
    }

    this.data = resolved.data.data;
    this.totalItemsInDb = resolved.data.metadata.total;
    this.itemsPerPage = resolved.data.metadata.perPage;

    const queryparams$ = this.activatedRoute.queryParams.subscribe(params => {
      this.currentPage = params['page'];
      let resolved = this.activatedRoute.snapshot.data['api'];

      if (!params['page'] || params['page'] > resolved.data.metadata.lastPage) {
        this.router.navigate(['.'], {
          relativeTo: this.activatedRoute,
          queryParams: { page: 1 },
        });
      }

      if (params['page']) {
        this.currentPage = params['page'];
        this.data = resolved.data.data;
        this.totalItemsInDb = resolved.data.metadata.total;
        this.itemsPerPage = resolved.data.metadata.perPage;
        if (this.pagination) {
          this.pagination.refresh(
            this.currentPage,
            this.totalItemsInDb,
            this.itemsPerPage
          );
        }
      }
    });

    this.subscriptions.push(queryparams$);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(o => o.unsubscribe());
  }

  public onSearchSubmit(event: Event): void {
    event.preventDefault();
    this.searchUser(this.searchControl.value);
  }

  public searchUser(value: string): void {
    this.search.value = value;
    this.search.applied = true;

    if (this.currentPage != 1) {
      this.router.navigate(['/private/users'], {
        queryParams: { page: 1 },
      });
    } else {
      this.loading.set(true);
      const searchUser$ = this.usersService
        .searchUser$(value, 1)
        .pipe(finalize(() => this.loading.set(false)))
        .subscribe(
          res => {
            this.data = res.data;
            this.totalItemsInDb = res.metadata.total;
            this.itemsPerPage = res.metadata.perPage;
            this.currentPage = 1;
            if (this.pagination) {
              this.pagination.refresh(
                this.currentPage,
                this.totalItemsInDb,
                this.itemsPerPage
              );
            }
          },
          error => {
            this.toastr.error(error?.error?.message, 'Error searching users');
          }
        );
      this.subscriptions.push(searchUser$);
    }
  }

  public onFileChange(event) {
    if (event.target.files.length > 1) {
      this.toastr.warning('You can upload only one file');
    }

    if (event.target.files[0]) {
      const parseCsvSub = this.ngxCsvParser
        .parse(event.target.files[0], {
          header: true,
          delimiter: ';',
          encoding: 'utf8',
        })
        .pipe(finalize(() => (this.csvFileRef.nativeElement.value = '')))
        .subscribe({
          next: (result: Array<User>): void => {
            const commaSeparatedHeaderError = CSV_HEADERS.join(',');
            const hasParseError =
              Object.keys(result[0])[0] === commaSeparatedHeaderError;

            this.openModalCsvResult(hasParseError ? [] : result);
          },
          error: (error: NgxCSVParserError): void => {
            this.toastr.error(error?.message, 'Error parsing file');
            console.log('Error parsing file', error);
          },
        });

      this.subscriptions.push(parseCsvSub);
    }
  }

  private openModalCsvResult(data: Array<User>) {
    const { closed$ } = this.dialogService.open(UploadResultComponent, {
      size: 'xl',
      inputs: { data },
    });
    this.refreshListOnSuccess(closed$);
  }

  public clearSearch(): void {
    this.initializeSearch();
    this.searchControl.setValue('');
    if (this.currentPage != 1) {
      this.router.navigate(['/private/users'], {
        queryParams: { page: 1 },
      });
    } else {
      this.getall();
    }
  }

  public openModalEdit(user: User) {
    this.openModalUser(user, 'edit');
  }

  public openModalCreate() {
    this.openModalUser(null, 'create');
  }

  public openModalDelete(user: User) {
    this.openModalUser(user, 'delete');
  }

  private openModalUser(user: User | null, action: ModalAction) {
    const { closed$ } = this.dialogService.open(ModalUserComponent, {
      size: 'lg',
      inputs: { user: user ? structuredClone(user) : null, action },
    });
    this.refreshListOnSuccess(closed$);
  }

  private refreshListOnSuccess(
    closed$: Observable<DialogCloseReason | undefined>
  ) {
    const onClose$ = closed$.subscribe(reason => {
      if (reason === 'success') {
        this.getall();
      }
    });

    this.subscriptions.push(onClose$);
  }

  private getall() {
    this.loading.set(true);

    if (this.search.applied) {
      const searchUser$ = this.usersService
        .searchUser$(this.search.value, this.currentPage)
        .pipe(finalize(() => this.loading.set(false)))
        .subscribe(
          res => {
            this.data = res.data;
            this.totalItemsInDb = res.metadata.total;
            this.itemsPerPage = res.metadata.perPage;
            if (this.pagination) {
              this.pagination.refresh(
                this.currentPage,
                this.totalItemsInDb,
                this.itemsPerPage
              );
            }
          },
          error => {
            this.toastr.error(error?.error?.message, 'Error searching users');
          }
        );
      this.subscriptions.push(searchUser$);
    } else {
      const all$ = this.usersService
        .getAll$(this.currentPage)
        .pipe(finalize(() => this.loading.set(false)))
        .subscribe({
          next: res => {
            this.data = res.data;
            this.totalItemsInDb = res.metadata.total;
            this.itemsPerPage = res.metadata.perPage;
            if (this.pagination) {
              this.pagination.refresh(
                this.currentPage,
                this.totalItemsInDb,
                this.itemsPerPage
              );
            }
          },
          error: error => {
            this.toastr.error(error?.error?.message, 'Error loading users');
          },
        });
      this.subscriptions.push(all$);
    }
  }

  private initializeSearch() {
    this.search = {
      value: '',
      applied: false,
    };
  }
}
